import { useEffect, useState } from "react";
import { FormikValues } from "formik";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../app/store";
import { useAppSelector } from "../../app/hooks";
import { onUpdateTheme } from "../../reducers/activeTemplateReducer";
import { Checkbox } from "@allaria-tech/matter/Checkbox";
import { Alert } from "@allaria-tech/matter/Alert";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import InteractiveInformation from "../InteractiveInformation/InteractiveInformation";
import { asCuit, genericToastifyError } from "../../utils";
import { initAppCheck } from "../../utils/appCheck";
import { sendCuotapartistaRequest } from "../../connectors/functions";
import {
  deleteBusinessDraft,
  getLastAllariaAccount,
  LastAllariaAccountResponse,
} from "../../connectors/connectors";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import type { FunctionsError } from "firebase/functions";

import styles from "./ActiveAccountSummary.module.scss";

interface ActiveAccountSummaryProps {
  title: string;
  subtitle: string | React.ReactNode;
  primaryText: string;
  values: FormikValues;
  dispatch: AppDispatch;
  resetForm: () => void;
}

/**
 * Shown when a draft with the same CUIT already exists (ESCO data).
 * Instead of resuming the onboarding, we display the data we already have
 * and the user only requests the "cuotapartista" account opening.
 */
const ActiveAccountSummary = ({
  title,
  subtitle,
  primaryText,
  values,
  dispatch,
  resetForm,
}: ActiveAccountSummaryProps) => {
  const { summaryContainer, field, alertRow, termsRow, checkboxLabel } = styles;
  const navigate = useNavigate();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const theme = useAppSelector((state) => state.activeTemplate.theme);
  const draftUUID = useAppSelector((state) => state.onboardingData.draftUUID);

  //Started here so the token is ready by the time the user hits the button
  useEffect(() => {
    initAppCheck();
  }, []);

  const {
    businessInformation,
    name,
    lastName,
    referrer,
    email,
    allariaAccount,
  } = values || {};

  const [fetchedAccount, setFetchedAccount] =
    useState<LastAllariaAccountResponse>();
  const [loadingAccount, setLoadingAccount] = useState(false);

  const cuit = businessInformation?.cuit;
  const account: LastAllariaAccountResponse | undefined =
    allariaAccount?.accountId ? allariaAccount : fetchedAccount;

  //Sessions persisted before the account was saved to the store land here
  //without it, and the function rejects a request with no legal name
  useEffect(() => {
    if (allariaAccount?.accountId || !cuit) return;

    setLoadingAccount(true);
    getLastAllariaAccount({ cuit })
      .then(({ data }) => {
        setFetchedAccount(data);
        dispatch(onUpdateFormData({ allariaAccount: data }));
      })
      .catch((error) => {
        console.log(error, "Error on getLastAllariaAccount");
        Sentry.captureException(error, {
          tags: { flow: "cuotapartista_request" },
          extra: { cuit },
        });
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      })
      .finally(() => setLoadingAccount(false));
  }, [allariaAccount?.accountId, cuit, dispatch]);

  const representative = [name, lastName].filter(Boolean).join(" ");
  const accountNumber = account?.accountId;
  const legalName = account?.description;

  const rows = [
    { label: "Razón social", value: legalName },
    {
      label: "CUIT",
      value: businessInformation?.cuit ? asCuit(businessInformation.cuit) : "",
    },
    { label: "Cuenta comitente", value: accountNumber },
    { label: "Representante legal", value: representative },
  ];

  //This flow has its own terms, not the company ones from getCompanyData
  const termsUrl = `${import.meta.env.VITE_BASE_URL_STATIC}/terms/Terminos%20y%20Condiciones%20FAL.pdf`;

  const onRequestOpening = async () => {
    //Same values the function writes to the spreadsheet, kept aside so we can
    //report them if the write fails
    const spreadsheetRow = {
      //Fetched in onAuthorize, before this screen renders
      accountNumber: accountNumber ?? "",
      cuit: businessInformation?.cuit ?? "",
      legalName: legalName ?? "",
      representative,
      email: email ?? "",
      referrer: referrer ?? "",
    };

    try {
      //The request is only registered in the spreadsheet, so we do not
      //continue if it fails
      await sendCuotapartistaRequest(spreadsheetRow);
    } catch (error) {
      console.log(error, "Error on sendCuotapartistaRequest");

      const { code, message, details } = (error ?? {}) as FunctionsError;
      //The function sends the underlying Sheets error in the details
      const reason = (details as { reason?: string } | undefined)?.reason;

      //A failed write loses the request: this is the only record left of it
      Sentry.captureException(error, {
        tags: {
          "onboarding.type": "JURIDICAL",
          flow: "cuotapartista_request",
          "function.code": code ?? "unknown",
        },
        extra: {
          spreadsheetRow,
          reason: reason ?? "unknown",
          functionMessage: message,
        },
      });

      showToastMessage(genericToastifyError, {
        containerId: "informationAlert",
        type: "ERROR",
      });
      return;
    }

    //The draft would stay open forever and count as an abandoned onboarding.
    //Deleted after writing to the spreadsheet, never before: that row is the
    //only record of the request
    try {
      if (draftUUID) await deleteBusinessDraft({ draftUUID });
    } catch (error) {
      //Already deleted: nothing to report
      const isNotFound =
        error instanceof AxiosError && error.response?.status === 404;

      if (!isNotFound) {
        console.log(error, "Error on deleteBusinessDraft");
        //Does not block the user: the request is already in the spreadsheet
        Sentry.captureException(error, {
          tags: { flow: "cuotapartista_request" },
          extra: { draftUUID, spreadsheetRow },
        });
      }
    }

    //The onboarding ends here, so we clean up the store and the form
    dispatch({ type: "reset-store" });
    // reset-store wipes activeTemplate.theme; re-dispatch it so the SuccessPage's
    // Layout keeps the company palette instead of falling back to the plain one
    if (theme) dispatch(onUpdateTheme(theme));
    resetForm();
    navigate("/registro-enviado?cuotapartista=true");
  };

  return (
    <InteractiveInformation
      alertType="INFO"
      title={title}
      subtitle={subtitle}
      allowOverflowScroll
      content={
        <section className={summaryContainer}>
          {rows.map(({ label, value }) => (
            <div className={field} key={label}>
              <span>{label}</span>
              <p>{value || "-"}</p>
            </div>
          ))}
          <div className={alertRow}>
            <Alert variant="INFO" title="Importante">
              En tu carácter de representante legal, al hacer click en el botón
              de <b>solicitar apertura</b> solicitás formalmente la apertura de
              la cuenta cuotapartista y autorizás a <b>Allaria SA</b> a
              compartir la documentación e información del legajo de la entidad
              con <b>Allaria Fondos Administrados SGFCI S.A.</b>
            </Alert>
          </div>
          <div className={termsRow}>
            <Checkbox
              testId="terminos-y-condiciones"
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              label={
                <div className={checkboxLabel}>
                  Acepto los{" "}
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Términos y Condiciones.
                  </a>
                </div>
              }
            />
          </div>
        </section>
      }
      primaryButton={{
        text: primaryText,
        onClick: onRequestOpening,
        disabled: !acceptedTerms || loadingAccount || !legalName,
      }}
    />
  );
};

export default ActiveAccountSummary;
